import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";

const FLAGS = {
  "Mexico": "🇲🇽", "South Africa": "🇿🇦", "South Korea": "🇰🇷",
  "Czech Republic": "🇨🇿",
  "Canada": "🇨🇦", "Switzerland": "🇨🇭", "Qatar": "🇶🇦",
  "Bosnia & Herzegovina": "🇧🇦",
  "Brazil": "🇧🇷", "Morocco": "🇲🇦", "Scotland": "🏴󠁧󠁢󠁳󠁣󠁴󠁿", "Haiti": "🇭🇹",
  "USA": "🇺🇸", "Paraguay": "🇵🇾", "Australia": "🇦🇺", "Turkey": "🇹🇷",
  "Germany": "🇩🇪", "Ivory Coast": "🇨🇮", "Ecuador": "🇪🇨", "Curaçao": "🇨🇼",
  "Netherlands": "🇳🇱", "Tunisia": "🇹🇳", "Japan": "🇯🇵", "Sweden": "🇸🇪",
  "Belgium": "🇧🇪", "Egypt": "🇪🇬", "Iran": "🇮🇷", "New Zealand": "🇳🇿",
  "Spain": "🇪🇸", "Cape Verde": "🇨🇻", "Saudi Arabia": "🇸🇦", "Uruguay": "🇺🇾",
  "France": "🇫🇷", "Senegal": "🇸🇳", "Norway": "🇳🇴", "Iraq": "🇮🇶",
  "Argentina": "🇦🇷", "Algeria": "🇩🇿", "Austria": "🇦🇹", "Jordan": "🇯🇴",
  "Portugal": "🇵🇹", "Colombia": "🇨🇴", "Uzbekistan": "🇺🇿", "DR Congo": "🇨🇩",
  "England": "🏴󠁧󠁢󠁥󠁮󠁧󠁿", "Croatia": "🇭🇷", "Ghana": "🇬🇭", "Panama": "🇵🇦",
};

function getFlag(team) {
  return FLAGS[team] || "🏳️";
}

function isPredictionOpen(kickoff) {
  const kickoffTime = new Date(kickoff);
  const deadline = new Date(kickoffTime.getTime() - 60 * 60 * 1000);
  return new Date() < deadline;
}

function formatDeadline(kickoff) {
  const kickoffTime = new Date(kickoff);
  const deadline = new Date(kickoffTime.getTime() - 60 * 60 * 1000);
  return deadline.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) + " BST";
}

export default function Predict() {
  const { matchId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const [match, setMatch] = useState(null);
  const [winner, setWinner] = useState("");
  const [homeScore, setHomeScore] = useState("");
  const [awayScore, setAwayScore] = useState("");
  const [goalscorer, setGoalscorer] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [existingPrediction, setExistingPrediction] = useState(null);
  const [timeLeft, setTimeLeft] = useState("");

  // Squad state
  const [squads, setSquads] = useState({ home: [], away: [] });
  const [goalscorerSearch, setGoalscorerSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  // Fetch match, squads and existing prediction
  useEffect(() => {
    async function fetchData() {
      const matchSnap = await getDoc(doc(db, "matches", matchId));
      if (!matchSnap.exists()) {
        setLoading(false);
        return;
      }
      const matchData = { id: matchSnap.id, ...matchSnap.data() };
      setMatch(matchData);

      const [homeSnap, awaySnap] = await Promise.all([
        getDoc(doc(db, "squads", matchData.homeTeam)),
        getDoc(doc(db, "squads", matchData.awayTeam)),
      ]);
      setSquads({
        home: homeSnap.exists() ? (homeSnap.data().players || []) : [],
        away: awaySnap.exists() ? (awaySnap.data().players || []) : [],
      });

      const predId = `${currentUser.uid}_${matchId}`;
      const predSnap = await getDoc(doc(db, "predictions", predId));
      if (predSnap.exists()) {
        const data = predSnap.data();
        setExistingPrediction(data);
        setWinner(data.winner);
        setHomeScore(data.homeScore.toString());
        setAwayScore(data.awayScore.toString());
        setGoalscorer(data.goalscorer || "");
      }

      setLoading(false);
    }
    fetchData();
  }, [matchId, currentUser]);

  // Countdown timer
  useEffect(() => {
    if (!match) return;

    function calculateTimeLeft() {
      const kickoffTime = new Date(match.kickoff);
      const deadline = new Date(kickoffTime.getTime() - 60 * 60 * 1000);
      const diff = deadline - new Date();

      if (diff <= 0) {
        setTimeLeft("Predictions closed");
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m`);
      } else {
        setTimeLeft(`${minutes}m`);
      }
    }

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 60000);
    return () => clearInterval(interval);
  }, [match]);

  // Auto-set winner from scores
  useEffect(() => {
    if (homeScore !== "" && awayScore !== "" && match) {
      const h = Number(homeScore);
      const a = Number(awayScore);
      if (h > a) setWinner(match.homeTeam);
      else if (a > h) setWinner(match.awayTeam);
      else setWinner("Draw");

      // Auto-clear goalscorer if 0-0
      if (h === 0 && a === 0) {
        setGoalscorer("");
        setGoalscorerSearch("");
      }
    }
  }, [homeScore, awayScore, match]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!winner) return setError("Please enter a score first.");
    if (homeScore === "" || awayScore === "") return setError("Please enter a score.");
    setSubmitting(true);
    setError("");
    try {
      const predictionId = `${currentUser.uid}_${matchId}`;
      await setDoc(doc(db, "predictions", predictionId), {
        userId: currentUser.uid,
        matchId,
        winner,
        homeScore: Number(homeScore),
        awayScore: Number(awayScore),
        goalscorer,
        pointsEarned: 0,
        createdAt: existingPrediction ? existingPrediction.createdAt : serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setSuccess(true);
      setTimeout(() => navigate("/events"), 1500);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  }

  const filteredHome = [...squads.home]
    .filter((p) => p.toLowerCase().includes(goalscorerSearch.toLowerCase()))
    .sort();

  const filteredAway = [...squads.away]
    .filter((p) => p.toLowerCase().includes(goalscorerSearch.toLowerCase()))
    .sort();

  const isNilNil = Number(homeScore) === 0 && Number(awayScore) === 0 && homeScore !== "" && awayScore !== "";
  const noResults = goalscorerSearch && filteredHome.length === 0 && filteredAway.length === 0;
  const isClosed = timeLeft === "Predictions closed";

  if (loading) return (
    <div className="min-h-screen" style={{ backgroundColor: "#F0F1EC" }}>
      <Navbar />
      <p className="font-condensed text-gray-400 p-10 text-xl">Loading match...</p>
    </div>
  );

  if (!match) return (
    <div className="min-h-screen" style={{ backgroundColor: "#F0F1EC" }}>
      <Navbar />
      <p className="font-condensed text-gray-400 p-10 text-xl">Match not found.</p>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F0F1EC" }}>
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-8 md:py-10">

        {/* Header */}
        <div className="mb-8 fade-up">
          <p className="font-condensed text-gray-500 tracking-widest uppercase text-sm mb-2">
            {existingPrediction ? "Edit Your Prediction" : "Make Your Prediction"}
          </p>

          {/* Title row — stacks on mobile, side by side on desktop */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
            <h2
              className="font-display tracking-wider leading-tight"
              style={{ color: "#0A0B0F", fontSize: "clamp(1.6rem, 6vw, 3rem)" }}
            >
              {match.homeTeam} <span className="text-gray-300">VS</span> {match.awayTeam}
            </h2>

            {timeLeft && (
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl self-start flex-shrink-0"
                style={{
                  backgroundColor: isClosed ? "#FFF0F0" : "#0A0B0F",
                  border: isClosed ? "1px solid #FFCCCC" : "none",
                }}
              >
                <span className="text-lg">⏱️</span>
                <span
                  className="font-display text-xl md:text-2xl tracking-wider"
                  style={{ color: isClosed ? "#CC0000" : "#C8FF00" }}
                >
                  {timeLeft}
                </span>
                {!isClosed && (
                  <span className="font-condensed text-xs tracking-widest uppercase" style={{ color: "#555" }}>
                    left
                  </span>
                )}
              </div>
            )}
          </div>

          <p className="font-condensed text-gray-400 tracking-wide text-sm md:text-base">
            {new Date(match.kickoff).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
            {" · "}
            Kick-off {new Date(match.kickoff).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} BST
            {" · "}
            <span style={{ color: "#FF5C00" }}>Closes {formatDeadline(match.kickoff)}</span>
          </p>
        </div>

        {/* Alerts */}
        {success && (
          <div className="mb-6 px-4 py-4 rounded-xl font-condensed tracking-wide text-lg"
            style={{ backgroundColor: "#EDFFD0", border: "1px solid #C8FF00", color: "#3A6600" }}>
            ✓ Prediction saved! Redirecting...
          </div>
        )}
        {error && (
          <div className="mb-6 px-4 py-3 rounded-xl font-condensed tracking-wide text-red-600"
            style={{ backgroundColor: "#FFF0F0", border: "1px solid #FFCCCC" }}>
            {error}
          </div>
        )}

        {!isPredictionOpen(match.kickoff) ? (
          <div className="card p-10 text-center fade-up">
            <div className="font-display text-6xl mb-4" style={{ color: "#E0E2DB" }}>🔒</div>
            <h3 className="font-display text-3xl tracking-wider mb-2" style={{ color: "#0A0B0F" }}>
              PREDICTIONS CLOSED
            </h3>
            <p className="font-condensed text-gray-400 tracking-wide text-lg">
              The prediction deadline for this match has passed.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 fade-up fade-up-delay-1">

{/* Squad not finalised warning — shows until May 30th 2026 */}
{new Date() < new Date("2026-05-30") && (
  <div className="px-4 py-3 rounded-xl font-condensed tracking-wide text-sm"
    style={{ backgroundColor: "#FFF3E0", border: "1px solid #FFD0A0", color: "#B85C00" }}>
    ⚠ Official World Cup squads are not confirmed until <strong>May 30th</strong>. You can make your score prediction now but we recommend waiting until after May 30th before selecting a first goalscorer — squads will be updated once confirmed.
  </div>
)}

            {/* Score */}
            <div className="card p-4 md:p-6">
              <h3 className="font-condensed text-gray-500 tracking-widest uppercase text-sm mb-5">
                Predicted Score
              </h3>
              <div className="flex items-start gap-3">

                {/* Home team */}
                <div className="flex-1 text-center min-w-0">
                  <div className="mb-3">
                    <span className="text-2xl block mb-1">{getFlag(match.homeTeam)}</span>
                    <span
                      className="font-condensed text-gray-500 tracking-wide text-sm block leading-tight"
                      style={{ wordBreak: "break-word" }}
                    >
                      {match.homeTeam}
                    </span>
                  </div>
                  <input
                    type="number" min="0" max="20"
                    value={homeScore}
                    onChange={(e) => setHomeScore(e.target.value)}
                    className="w-full rounded-xl px-2 py-3 md:py-4 text-center"
                    style={{
                      fontSize: "clamp(1.8rem, 8vw, 2.5rem)",
                      fontFamily: "'Bebas Neue', sans-serif",
                      color: "#0A0B0F",
                    }}
                  />
                </div>

                {/* Divider */}
                <div
                  className="font-display text-gray-300 flex-shrink-0"
                  style={{ fontSize: "2rem", paddingTop: "4rem" }}
                >
                  —
                </div>

                {/* Away team */}
                <div className="flex-1 text-center min-w-0">
                  <div className="mb-3">
                    <span className="text-2xl block mb-1">{getFlag(match.awayTeam)}</span>
                    <span
                      className="font-condensed text-gray-500 tracking-wide text-sm block leading-tight"
                      style={{ wordBreak: "break-word" }}
                    >
                      {match.awayTeam}
                    </span>
                  </div>
                  <input
                    type="number" min="0" max="20"
                    value={awayScore}
                    onChange={(e) => setAwayScore(e.target.value)}
                    className="w-full rounded-xl px-2 py-3 md:py-4 text-center"
                    style={{
                      fontSize: "clamp(1.8rem, 8vw, 2.5rem)",
                      fontFamily: "'Bebas Neue', sans-serif",
                      color: "#0A0B0F",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* First Goalscorer */}
            <div className="card p-4 md:p-6">
              <h3 className="font-condensed text-gray-500 tracking-widest uppercase text-sm mb-1">
                First Goalscorer
                <span className="ml-2 text-xs px-2 py-0.5 rounded-full font-condensed"
                  style={{ backgroundColor: "#EDFFD0", color: "#3A6600" }}>
                  +3 BONUS PTS
                </span>
              </h3>
              <p className="font-condensed text-gray-400 text-sm tracking-wide mb-5">
                Optional — select a player or choose no goalscorer
              </p>

              {isNilNil ? (
                <div className="px-4 py-3 rounded-xl font-condensed tracking-wide text-center"
                  style={{ backgroundColor: "#F5F6F2", border: "1px solid #E0E2DB", color: "#999" }}>
                  No first goalscorer — 0-0 draw selected
                </div>
              ) : (
                <div className="relative" ref={dropdownRef}>

                  {/* Trigger button */}
                  <button
                    type="button"
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="w-full text-left px-4 py-3 rounded-xl font-condensed text-lg tracking-wide flex justify-between items-center"
                    style={{
                      backgroundColor: goalscorer ? "#EDFFD0" : "#FFFFFF",
                      border: goalscorer ? "1px solid #C8FF00" : "1px solid #E0E2DB",
                      color: goalscorer ? "#3A6600" : "#888",
                    }}
                  >
                    <span>{goalscorer ? `⚽ ${goalscorer}` : "No first goalscorer"}</span>
                    <span style={{ color: "#aaa" }}>{showDropdown ? "▲" : "▼"}</span>
                  </button>

                  {/* Dropdown */}
                  {showDropdown && (
                    <div
                      className="absolute z-20 w-full mt-1 rounded-xl overflow-hidden shadow-xl"
                      style={{ backgroundColor: "#FFFFFF", border: "1px solid #E0E2DB", maxHeight: "320px", overflowY: "auto" }}
                    >
                      {/* Search */}
                      <div className="px-3 py-3 sticky top-0"
                        style={{ backgroundColor: "#FFFFFF", borderBottom: "1px solid #E0E2DB" }}>
                        <input
                          type="text"
                          placeholder="Search players..."
                          value={goalscorerSearch}
                          onChange={(e) => setGoalscorerSearch(e.target.value)}
                          className="w-full rounded-lg px-3 py-2 text-base"
                          style={{ border: "1px solid #E0E2DB" }}
                          autoComplete="off"
                        />
                      </div>

                      {/* No goalscorer */}
                      {!goalscorerSearch && (
                        <button
                          type="button"
                          onClick={() => { setGoalscorer(""); setGoalscorerSearch(""); setShowDropdown(false); }}
                          className="w-full text-left px-4 py-3 font-condensed tracking-wide transition-colors hover:bg-gray-50"
                          style={{
                            color: !goalscorer ? "#3A6600" : "#888",
                            backgroundColor: !goalscorer ? "#FAFFF5" : "transparent",
                            borderBottom: "2px solid #E0E2DB",
                          }}
                        >
                          {!goalscorer && <span className="mr-2">✓</span>}
                          No first goalscorer
                        </button>
                      )}

                      {/* Home team */}
                      {filteredHome.length > 0 && (
                        <div className="px-4 py-2 font-condensed text-xs tracking-widest uppercase"
                          style={{ backgroundColor: "#F8F9F5", color: "#888", borderBottom: "1px solid #E0E2DB" }}>
                          {getFlag(match.homeTeam)} {match.homeTeam}
                        </div>
                      )}
                      {filteredHome.map((player) => (
                        <button
                          key={`home-${player}`}
                          type="button"
                          onClick={() => { setGoalscorer(player); setGoalscorerSearch(""); setShowDropdown(false); }}
                          className="w-full text-left px-4 py-3 font-condensed tracking-wide transition-colors hover:bg-gray-50"
                          style={{
                            color: "#0A0B0F",
                            backgroundColor: goalscorer === player ? "#FAFFF5" : "transparent",
                            borderBottom: "1px solid #F5F6F2",
                          }}
                        >
                          {goalscorer === player && <span className="mr-2" style={{ color: "#3A6600" }}>✓</span>}
                          {player}
                        </button>
                      ))}

                      {/* Away team */}
                      {filteredAway.length > 0 && (
                        <div className="px-4 py-2 font-condensed text-xs tracking-widest uppercase"
                          style={{ backgroundColor: "#F8F9F5", color: "#888", borderBottom: "1px solid #E0E2DB" }}>
                          {getFlag(match.awayTeam)} {match.awayTeam}
                        </div>
                      )}
                      {filteredAway.map((player) => (
                        <button
                          key={`away-${player}`}
                          type="button"
                          onClick={() => { setGoalscorer(player); setGoalscorerSearch(""); setShowDropdown(false); }}
                          className="w-full text-left px-4 py-3 font-condensed tracking-wide transition-colors hover:bg-gray-50"
                          style={{
                            color: "#0A0B0F",
                            backgroundColor: goalscorer === player ? "#FAFFF5" : "transparent",
                            borderBottom: "1px solid #F5F6F2",
                          }}
                        >
                          {goalscorer === player && <span className="mr-2" style={{ color: "#3A6600" }}>✓</span>}
                          {player}
                        </button>
                      ))}

                      {/* No squad data */}
                      {squads.home.length === 0 && squads.away.length === 0 && (
                        <div className="px-4 py-6 text-center font-condensed text-gray-400 tracking-wide">
                          Squad data not available yet
                        </div>
                      )}

                      {/* No results */}
                      {noResults && (
                        <div className="px-4 py-4 font-condensed text-gray-400 tracking-wide text-center">
                          No players found matching "{goalscorerSearch}"
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Points breakdown */}
            <div className="rounded-xl p-4 md:p-5" style={{ backgroundColor: "#FFFFFF", border: "1px solid #E0E2DB" }}>
              <h3 className="font-condensed text-gray-400 tracking-widest uppercase text-xs mb-4">
                Points Available
              </h3>
              {[
                { label: "Correct winner", pts: "2 pts" },
                { label: "Exact score", pts: "5 pts" },
                { label: "First goalscorer", pts: "+3 pts" },
              ].map((row) => (
                <div key={row.label} className="flex justify-between py-1">
                  <span className="font-condensed text-gray-500 tracking-wide">{row.label}</span>
                  <span className="font-condensed font-bold tracking-wide" style={{ color: "#3A6600" }}>
                    {row.pts}
                  </span>
                </div>
              ))}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-lime w-full py-4 rounded-xl text-xl"
            >
              {submitting ? "SAVING..." : existingPrediction ? "UPDATE PREDICTION" : "SUBMIT PREDICTION"}
            </button>

          </form>
        )}
      </div>
    </div>
  );
}
